import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export function Reveal({ children, delay = 0, className = "", y = 24 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, className = "", testid }) {
  return (
    <span data-testid={testid} className={`label-eyebrow ${className}`}>
      {children}
    </span>
  );
}

const base =
  "inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 font-body text-sm font-semibold tracking-wide transition-all duration-300";

export function PrimaryButton({ to, href, children, className = "", testid, onClick, type }) {
  const cls = `${base} bg-blush text-ink hover:bg-bronze hover:text-ivory hover:-translate-y-0.5 ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls} target="_blank" rel="noopener noreferrer">{children}</a>;
  return <button type={type || "button"} onClick={onClick} data-testid={testid} className={cls}>{children}</button>;
}

export function GhostButton({ to, href, children, className = "", testid, onClick, type, download }) {
  const cls = `${base} border border-champagne/70 text-ink hover:bg-champagne/15 hover:-translate-y-0.5 ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  if (href) return <a href={href} download={download} data-testid={testid} className={cls} target={download ? undefined : "_blank"} rel="noopener noreferrer">{children}</a>;
  return <button type={type || "button"} onClick={onClick} data-testid={testid} className={cls}>{children}</button>;
}

export function PageHero({ eyebrow, title, lines, sub, image, children }) {
  return (
    <section className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-blush/35 via-ivory/80 to-ivory" />
      {image && (
        <div className="absolute right-0 top-0 -z-10 h-full w-1/2 opacity-25 [mask-image:linear-gradient(to_left,black,transparent)]">
          <img src={image} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {eyebrow && <Reveal><Eyebrow>{eyebrow}</Eyebrow></Reveal>}
        <Reveal delay={0.05}>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            {title}
          </h1>
        </Reveal>
        {lines && (
          <Reveal delay={0.12}>
            <p className="mt-6 font-accent text-2xl md:text-3xl italic text-bronze">{lines}</p>
          </Reveal>
        )}
        {sub && (
          <Reveal delay={0.18}>
            <p className="mt-6 font-body text-base md:text-lg leading-relaxed text-ink/75 max-w-2xl">{sub}</p>
          </Reveal>
        )}
        {children}
      </div>
    </section>
  );
}
